import { Zap } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import {
  formatoCantidad,
  formatoFecha,
  formatoFechaYHora,
  formatoNumero,
} from '@/lib/utils/formato-precio';
import { hrefDeLaEmpresa } from '@/router/href-de-la-empresa';
import { hrefsDeCompite } from '@/features/compite/lib/rutas';
import type { AppliedStrategy } from '../types/strategy-applications.types';
import { permisoEnPalabras } from './permiso-de-estrategia';
import { METODOS } from './nueva-estrategia/paso-2-metodo';
import { NOMBRE_CADENCIA } from './nueva-estrategia/paso-3-cuando';
import { estaVencida, NUMERO_DEL_METODO } from './nueva-estrategia/borrador';
import { EncabezadoDeVentana } from '@/features/compite/ui/ventana-compite';

/**
 * La ficha de la estrategia (plan 3, Tarea 6 · P3-D5): qué hizo en su ÚLTIMA
 * corrida, con los contadores que la base ya guarda (`lastRun*`,
 * `strategy-evaluation.ts:guardarResumen`) y los que esperan decisión hoy. No hay
 * historial de corridas: si hace falta, después.
 *
 * 🔴 Los números NO se suman entre sí y no se los hace sumar: los «sin datos» no
 * cuentan como revisados, un producto limitado por una regla también puede
 * haberse aplicado, y los que esperan tu sí pueden venir de corridas anteriores.
 * Inventar un «sin cambio» restando daría un número que no existe.
 *
 * Una corrida que falló (`lastRunStatus === 'FAILED'`) no muestra números: los
 * que hubiera serían a medias.
 */
const num = formatoCantidad;
/** «25 sept, 00:57»: la hora en 24 h, nunca «12:57 a. m.» (pulido, spec 232). */
const cuando = formatoFechaYHora;

export function ResumenCorrida({
  estrategia: e,
  compacta = false,
}: {
  estrategia: AppliedStrategy;
  compacta?: boolean;
}) {
  if (!e.lastRunAt) {
    return (
      <p data-testid="estado" className="text-xs text-muted-foreground">
        Todavía no corrió ninguna vez.
      </p>
    );
  }
  if (e.lastRunStatus === 'FAILED') {
    return (
      <p data-testid="estado" className="text-xs text-destructive">
        La última corrida ({cuando(e.lastRunAt)}) falló: no hay números que mostrar de ella.
      </p>
    );
  }

  // El cuarto valor dice de qué color va la cifra cuando no es cero: sólo esas se
  // colorean (pulido, spec 232). Lo que se revisó o se aplicó solo no pide nada.
  const contadores: Array<[string, string, number, 'alerta' | 'bloqueo' | null]> = [
    ['evaluados', 'revisados', e.lastRunEvaluated, null],
    ['aplicados', 'se aplicaron solos', e.lastRunApplied, null],
    ['limitados', 'los limitó una regla', e.lastRunHeld, 'alerta'],
    // H15 (27-sep): no «les faltó un dato», que en Hoy ya es «sin costo». Por qué
    // no se pudieron calcular lo dice la nota de la ficha, no el rótulo.
    ['sin-datos', 'no se pudieron calcular', e.lastRunNoData, 'alerta'],
    // Lote C1 (punto 9, D13): sólo en las que aplican solas; en una que siempre
    // pregunta serían siempre cero.
    ...(e.mode !== 'SUPERVISED'
      ? ([
          ['bajo-costo', 'frenados: quedaban debajo del costo', e.lastRunBelowCost ?? 0, 'bloqueo'],
          ['sin-costo', 'sin costo: te preguntan', e.lastRunNoCost ?? 0, 'alerta'],
        ] as Array<[string, string, number, 'alerta' | 'bloqueo' | null]>)
      : []),
  ];
  const tinta = (tono: 'alerta' | 'bloqueo' | null, n: number) =>
    tono && n > 0 ? (tono === 'bloqueo' ? 'text-bloqueo' : 'text-alerta') : null;
  // P2 (plan 2): lo que espera de una CERRADA es su vuelta (el barrido descartó
  // las otras). Antes del barrido, las que esperan son las normales (A1).
  const vencida = e.closedAt != null;

  if (compacta) {
    // La tarjeta: una sola línea, debajo de una raya fina (maqueta del pulido).
    return (
      <dl className="flex flex-wrap items-baseline gap-x-5 gap-y-1 border-t pt-2.5 text-xs text-muted-foreground">
        <div>
          <dt className="inline">Última corrida </dt>
          <dd className="inline">{cuando(e.lastRunAt)}</dd>
        </div>
        {contadores.map(([id, texto, n, tono]) => (
          <div key={id} className="flex items-baseline gap-1">
            <dd
              data-testid={id}
              className={cn('text-sm font-semibold tabular-nums text-foreground', tinta(tono, n))}
            >
              {num(n)}
            </dd>
            <dt>{texto}</dt>
          </div>
        ))}
      </dl>
    );
  }

  return (
    <div className="space-y-1">
      <p className="text-xs text-muted-foreground">
        Última corrida (hora de Bolivia): {cuando(e.lastRunAt)}
      </p>
      <dl className="grid grid-cols-2 gap-2 text-sm">
        {contadores.map(([id, texto, n, tono]) => (
          <div key={id} className="rounded-md border p-2">
            <dd data-testid={id} className={cn('font-semibold tabular-nums', tinta(tono, n))}>
              {num(n)}
            </dd>
            <dt className="text-xs text-muted-foreground">{texto}</dt>
          </div>
        ))}
        {/* Revisión T6: en la tarjeta (compacta) lo que espera ya lo dice su etiqueta
            «N esperan tu decisión»; por eso este contador sólo va en la ficha. */}
        <div className="rounded-md border p-2">
          <dd data-testid="esperan" className="font-semibold tabular-nums">
            {num(e.propuestasPendientes)}
          </dd>
          <dt className="text-xs text-muted-foreground">
            {vencida ? 'de la vuelta esperan tu sí' : 'esperan tu sí ahora'}
          </dt>
        </div>
      </dl>
      <p className="text-xs text-muted-foreground">
        Los números no se suman: a los que no se pudieron calcular no se los revisó, uno limitado
        por una regla puede haberse aplicado igual, y los que esperan tu sí pueden venir de corridas
        anteriores.
      </p>
      <p data-testid="por-que-no-se-calcularon" className="text-xs text-muted-foreground">
        A los que no se pudieron calcular les faltó competencia, costo o precio de lista, según el
        método.
      </p>
    </div>
  );
}

/**
 * C8 (27-sep): el número es el de la clave del método (`NUMERO_DEL_METODO`), no
 * el primero que aparezca en `strategyConfig` (Precio inteligente guarda
 * `weights`). Sin número: el de siempre «(por defecto)», como la tarjeta, salvo el
 * quinto método, que no tiene defecto en el backend: «—».
 */
export function metodoEnLlano(e: Pick<AppliedStrategy, 'strategy' | 'strategyConfig'>): string {
  const titulo = METODOS.find((m) => m.metodo === e.strategy)?.titulo;
  if (!titulo) return 'Fórmula propia';
  const campo = NUMERO_DEL_METODO[e.strategy];
  if (!campo) return titulo;
  const n = (e.strategyConfig as Record<string, unknown> | null)?.[campo.key];
  if (typeof n === 'number') return `${titulo} · ${formatoNumero(n)} %`;
  return campo.key === 'discountPercent'
    ? `${titulo} · —`
    : `${titulo} · ${campo.inicial} % (por defecto)`;
}

function alcanceEnLlano(e: AppliedStrategy): string {
  if (e.group.kind === 'MANUAL') {
    // Revisión final del plan 3, M3: «1 producto elegido», no «elegidos».
    const s = e.group.skus.length === 1 ? '' : 's';
    return `${num(e.group.skus.length)} producto${s} elegido${s}`;
  }
  const f = (e.group.filter ?? {}) as { categories?: string[]; brands?: string[] };
  const partes = [
    f.categories?.length ? `categoría ${f.categories.join(', ')}` : null,
    f.brands?.length ? `marca ${f.brands.join(', ')}` : null,
  ].filter(Boolean);
  const menos = e.group.excludedSkus?.length ? ` · menos ${num(e.group.excludedSkus.length)}` : '';
  return (partes.length ? partes.join(' y ') : 'por filtro') + menos;
}

export function FichaEstrategiaDialog({
  estrategia: e,
  onCerrar,
}: {
  estrategia: AppliedStrategy | null;
  onCerrar: () => void;
}) {
  return (
    <Dialog open={e !== null} onOpenChange={(v) => !v && onCerrar()}>
      <DialogContent className="max-w-xl">
        {e && (
          <>
            <EncabezadoDeVentana icono={Zap}>
              <DialogTitle>{e.name}</DialogTitle>
              <DialogDescription>Método: {metodoEnLlano(e)}</DialogDescription>
            </EncabezadoDeVentana>
            <dl className="grid grid-cols-[140px_1fr] gap-x-3 gap-y-1 text-sm">
              <dt className="text-muted-foreground">A quién alcanza</dt>
              <dd>{alcanceEnLlano(e)}</dd>
              <dt className="text-muted-foreground">Permiso</dt>
              <dd>{permisoEnPalabras(e.mode, e.askAboveChangePct ?? null)}</dd>
              <dt className="text-muted-foreground">Cada cuánto</dt>
              <dd>{NOMBRE_CADENCIA[e.cadence]}</dd>
              <dt className="text-muted-foreground">Hasta cuándo</dt>
              <dd>{e.validUntil ? formatoFecha(e.validUntil) : 'sin fecha de fin'}</dd>
              <dt className="text-muted-foreground">Estado</dt>
              <dd>
                {estaVencida(e) ? (
                  <>
                    venció el {formatoFecha(e.validUntil ?? e.closedAt!)}
                    {/* A1: la vuelta existe recién cuando el barrido la cerró. */}
                    {e.closedAt && (
                      <>
                        {' '}
                        ·{' '}
                        {/* Plan 2: la hoja de la vuelta se abre con `?vuelta=<id>` (choque 14b). */}
                        <a
                          href={hrefDeLaEmpresa(hrefsDeCompite.estrategias({ vuelta: e.id }))}
                          className="text-primary underline underline-offset-2"
                        >
                          Ver la vuelta
                        </a>
                      </>
                    )}
                  </>
                ) : e.isActive ? (
                  'activa'
                ) : (
                  'apagada'
                )}
              </dd>
            </dl>
            <ResumenCorrida estrategia={e} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
